import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, BarChart3, Lightbulb, Settings, TrendingUp, TrendingDown,
  CheckCircle, AlertCircle, ThumbsUp, ThumbsDown, Clock, Package,
  Trash2, Edit, Play, X
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';

export default function SkillDetail() {
  const { t } = useTranslation();
  const { skillId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [skill, setSkill] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [processingAction, setProcessingAction] = useState(false);
  const [skillMemory, setSkillMemory] = useState(null); // Phase 2

  useEffect(() => {
    fetchSkillDetails();
  }, [skillId]);

  const fetchSkillDetails = async () => {
    setLoading(true);
    try {
      // Try to get skill from installed skills first
      const mySkillsRes = await apiClient.get(`/skills/my-skills?filter=installed`);
      let skillData = mySkillsRes.data.find(s => s.skillPackageId === skillId);
      
      if (!skillData) {
        // Try to get from created skills
        const createdRes = await apiClient.get(`/skills/my-skills?filter=created`);
        skillData = createdRes.data.find(s => s.skillPackageId === skillId);
      }
      
      if (!skillData) {
        // Try to get from owned (library) skills
        const ownedRes = await apiClient.get(`/skills/my-skills?filter=owned`);
        skillData = ownedRes.data.find(s => s.skillPackageId === skillId);
      }
      
      if (!skillData) {
        setSkill(null);
        setLoading(false);
        toast.error(t('errors.skillNotFound'));
        return;
      }
      
      setSkill(skillData);

      const [analyticsRes, insightsRes, suggestionsRes] = await Promise.all([
        apiClient.get(`/skills/analytics/skill/${skillId}`).catch(() => ({ data: null })),
        apiClient.get(`/skills/learning/insights/${skillId}`).catch(() => ({ data: null })),
        apiClient.get(`/skills/learning/suggestions?status=pending`).catch(() => ({ data: [] }))
      ]);

      setAnalytics(analyticsRes.data);
      setInsights(insightsRes.data);
      
      // Filter suggestions for this skill
      const skillSuggestions = suggestionsRes.data.filter(s => s.skillPackageId === skillId);
      setSuggestions(skillSuggestions);
      
      // Get feedback if analytics exist
      if (analyticsRes.data?.feedback) {
        setFeedback(analyticsRes.data.feedback);
      }
      
      // Fetch skill memory (Phase 2)
      if (skillData.isInstalled) {
        try {
          const memoryRes = await apiClient.get(`/ai/skills/${skillId}/memory`);
          setSkillMemory(memoryRes.data);
        } catch (error) {
          console.log('No memory found for skill');
        }
      }
    } catch (error) {
      console.error('Failed to fetch skill details:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSuggestions = async () => {
    setProcessingAction(true);
    try {
      const response = await apiClient.post(`/skills/learning/generate-suggestions/${skillId}`);
      toast.success(`${response.data.generated} ${t('skills.suggestionsGenerated')}`);
      await fetchSkillDetails();
    } catch (error) {
      console.error('Failed to generate suggestions:', error);
      toast.error(error.response?.data?.detail || t('errors.operationFailed'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReviewSuggestion = async (suggestionId, action) => {
    setProcessingAction(true);
    try {
      await apiClient.post(`/skills/learning/suggestions/${suggestionId}/review`, { action });
      toast.success(action === 'approve' ? t('learning.suggestionApproved') : t('learning.suggestionRejected'));
      await fetchSkillDetails();
    } catch (error) {
      console.error('Failed to review suggestion:', error);
      toast.error(t('errors.operationFailed'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleUninstall = async () => {
    if (!window.confirm(t('skills.confirmUninstall'))) return;
    
    setProcessingAction(true);
    try {
      await apiClient.post(`/skills/uninstall`, { skillPackageId: skillId });
      toast.success(t('skills.uninstalled'));
      navigate('/personal/skills');
    } catch (error) {
      console.error('Failed to uninstall:', error);
      toast.error(t('errors.operationFailed'));
    } finally {
      setProcessingAction(false);
    }
  };

  const getHealthBadge = (health) => {
    const badges = {
      excellent: { color: 'bg-green-100 text-green-700 border-green-300', label: t('analytics.excellent'), icon: CheckCircle },
      good: { color: 'bg-blue-100 text-blue-700 border-blue-300', label: t('analytics.good'), icon: CheckCircle },
      fair: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', label: t('analytics.fair'), icon: AlertCircle },
      poor: { color: 'bg-red-100 text-red-700 border-red-300', label: t('analytics.poor'), icon: AlertCircle }
    };
    return badges[health] || badges.good;
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-5 h-5 text-green-600" />;
    if (trend === 'declining') return <TrendingDown className="w-5 h-5 text-red-600" />;
    return null;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">{t('skills.notFound')}</p>
          <Button className="mt-4" onClick={() => navigate('/personal/skills')}>
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  const healthBadge = analytics ? getHealthBadge(analytics.health) : null;
  const HealthIcon = healthBadge?.icon;

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/personal/skills')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">{skill.name}</h1>
          </div>
        </div>
        
        <Button onClick={() => navigate(`/personal/assistant?skill=${skillId}`)}>
          <Play className="w-4 h-4 mr-2" />
          {t('skills.useNow')}
        </Button>
      </div>

      {/* Quick Stats */}
      {analytics && (
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('skills.rating')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.ratings?.positivePercentage?.toFixed(1) || 0}%
                </p>
                <p className="text-xs text-gray-500">{analytics.totalFeedback} {t('skills.ratings')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${healthBadge.color.split(' ')[0]}`}>
                {HealthIcon && <HealthIcon className="w-6 h-6" />}
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('skills.health')}</p>
                <p className="text-2xl font-bold text-gray-900">{healthBadge.label}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Lightbulb className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('skills.suggestions')}</p>
                <p className="text-2xl font-bold text-purple-600">{suggestions.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {getTrendIcon(analytics.trend) || (
                <div className="p-3 bg-gray-100 rounded-xl">
                  <Clock className="w-6 h-6 text-gray-600" />
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">{t('skills.trend')}</p>
                <p className="text-2xl font-bold text-gray-900 capitalize">{analytics.trend}</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Card className="p-6">
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">{t('skills.overview')}</TabsTrigger>
            <TabsTrigger value="performance">{t('skills.performance')}</TabsTrigger>
            <TabsTrigger value="learning">
              🧠 Learning {skillMemory?.learnedRules?.length > 0 && `(${skillMemory.learnedRules.length})`}
            </TabsTrigger>
            <TabsTrigger value="suggestions">
              {t('skills.suggestions')} {suggestions.length > 0 && `(${suggestions.length})`}
            </TabsTrigger>
            <TabsTrigger value="settings">{t('skills.settings')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* Description */}
            <div>
              <h3 className="font-semibold text-lg mb-3">{t('skills.description')}</h3>
              <p className="text-gray-700">{skill.description || t('skills.noDescription')}</p>
            </div>

            {/* Examples */}
            {skill.examples && skill.examples.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-3">{t('skills.examples')}</h3>
                <div className="space-y-3">
                  {skill.examples.map((example, idx) => (
                    <Card key={idx} className="p-4 bg-gray-50">
                      <p className="text-sm text-gray-700">{example}</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Instructions */}
            {skill.instructions && (
              <div>
                <h3 className="font-semibold text-lg mb-3">{t('skills.instructions')}</h3>
                <Card className="p-4 bg-gray-50">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{skill.instructions}</p>
                </Card>
              </div>
            )}

            {/* Metadata */}
            {skill.category && (
              <div>
                <h3 className="font-semibold text-lg mb-3">{t('skills.metadata')}</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{skill.category}</Badge>
                  {skill.tags && skill.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="mt-6 space-y-6">
            {analytics ? (
              <>
                {/* Ratings Breakdown */}
                <div>
                  <h3 className="font-semibold text-lg mb-4">{t('skills.ratingsBreakdown')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4 bg-green-50 border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">{t('skills.positive')}</p>
                          <p className="text-2xl font-bold text-green-700">
                            {analytics.ratings?.positivePercentage?.toFixed(1) || 0}%
                          </p>
                        </div>
                        <ThumbsUp className="w-8 h-8 text-green-600" />
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {analytics.ratings?.positive || 0} {t('skills.ratings')}
                      </p>
                    </Card>

                    <Card className="p-4 bg-red-50 border-red-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">{t('skills.negative')}</p>
                          <p className="text-2xl font-bold text-red-700">
                            {analytics.ratings?.negativePercentage?.toFixed(1) || 0}%
                          </p>
                        </div>
                        <ThumbsDown className="w-8 h-8 text-red-600" />
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {analytics.ratings?.negative || 0} {t('skills.ratings')}
                      </p>
                    </Card>

                    <Card className="p-4 bg-gray-50 border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">{t('skills.neutral')}</p>
                          <p className="text-2xl font-bold text-gray-700">
                            {analytics.ratings?.neutralPercentage?.toFixed(1) || 0}%
                          </p>
                        </div>
                        <Clock className="w-8 h-8 text-gray-600" />
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {analytics.ratings?.neutral || 0} {t('skills.ratings')}
                      </p>
                    </Card>
                  </div>
                </div>

                {/* Insights */}
                {insights && insights.insights && (
                  <div>
                    <h3 className="font-semibold text-lg mb-4">{t('skills.insights')}</h3>
                    
                    {/* Patterns */}
                    {insights.insights.patterns && insights.insights.patterns.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('skills.patterns')}</h4>
                        <div className="space-y-2">
                          {insights.insights.patterns.map((pattern, idx) => (
                            <Card key={idx} className="p-3 bg-blue-50">
                              <p className="text-sm text-gray-700">{pattern.description}</p>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Prediction */}
                    {insights.insights.prediction && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('skills.prediction')}</h4>
                        <Card className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                          <div className="flex items-start gap-3">
                            <Lightbulb className="w-5 h-5 text-purple-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 mb-1">
                                {insights.insights.prediction.prediction === 'improving' && t('skills.improving')}
                                {insights.insights.prediction.prediction === 'declining' && t('skills.declining')}
                                {insights.insights.prediction.prediction === 'stable' && t('skills.stablePrediction')}
                              </p>
                              <p className="text-sm text-gray-700 mb-2">{insights.insights.prediction.reasoning}</p>
                              <p className="text-xs text-gray-600">
                                {t('skills.confidence')}: {((insights.insights?.prediction?.confidence || 0) * 100).toFixed(0)}%
                              </p>
                            </div>
                          </div>
                        </Card>
                      </div>
                    )}
                  </div>
                )}

                {/* Recent Feedback */}
                {feedback && feedback.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-4">{t('skills.recentFeedback')}</h3>
                    <div className="space-y-3">
                      {feedback.slice(0, 5).map((fb, idx) => (
                        <Card key={idx} className="p-4">
                          <div className="flex items-start gap-3">
                            {fb.rating === 'positive' ? (
                              <ThumbsUp className="w-5 h-5 text-green-600 mt-0.5" />
                            ) : (
                              <ThumbsDown className="w-5 h-5 text-red-600 mt-0.5" />
                            )}
                            <div className="flex-1">
                              {fb.feedbackText && (
                                <p className="text-sm text-gray-700 mb-1">{fb.feedbackText}</p>
                              )}
                              <p className="text-xs text-gray-500">
                                {new Date(fb.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">{t('skills.noPerformanceData')}</p>
                <p className="text-sm text-gray-500 mt-2">{t('skills.noPerformanceDataDesc')}</p>
              </div>
            )}
          </TabsContent>

          {/* Suggestions Tab */}
          <TabsContent value="suggestions" className="mt-6 space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">{t('skills.improvementSuggestions')}</h3>
              <Button
                onClick={handleGenerateSuggestions}
                disabled={processingAction || !analytics || analytics.totalFeedback < 3}
                size="sm"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                {t('skills.generateSuggestions')}
              </Button>
            </div>

            {suggestions.length > 0 ? (
              <div className="space-y-4">
                {suggestions.map((suggestion) => (
                  <Card key={suggestion.suggestionId} className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">{suggestion.title}</h4>
                          <Badge className={getPriorityColor(suggestion.priority)}>
                            {suggestion.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">{suggestion.description}</p>
                        
                        {suggestion.currentValue && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-600 mb-1">{t('skills.current')}:</p>
                            <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{suggestion.currentValue}</p>
                          </div>
                        )}
                        
                        {suggestion.suggestedValue && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-600 mb-1">{t('skills.suggested')}:</p>
                            <p className="text-sm text-green-700 bg-green-50 p-2 rounded">{suggestion.suggestedValue}</p>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">
                            {t('skills.confidence')}: <span className="font-medium">{((suggestion.confidenceScore || 0) * 100).toFixed(0)}%</span>
                          </span>
                          {suggestion.estimatedImprovement && (
                            <span className="text-green-600 font-medium">
                              {suggestion.estimatedImprovement}
                            </span>
                          )}
                        </div>
                        
                        {suggestion.reasoning && (
                          <p className="text-xs text-gray-600 mt-2 italic">{suggestion.reasoning}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-3 border-t">
                      <Button
                        size="sm"
                        onClick={() => handleReviewSuggestion(suggestion.suggestionId, 'approve')}
                        disabled={processingAction}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {t('learning.approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReviewSuggestion(suggestion.suggestionId, 'reject')}
                        disabled={processingAction}
                      >
                        <X className="w-4 h-4 mr-2" />
                        {t('learning.reject')}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">{t('skills.noSuggestions')}</p>
                <p className="text-sm text-gray-500 mt-2">{t('skills.noSuggestionsDesc')}</p>
                {analytics && analytics.totalFeedback < 3 && (
                  <p className="text-sm text-orange-600 mt-2">
                    {t('skills.needMoreFeedback', { count: 3 - analytics.totalFeedback })}
                  </p>
                )}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}


          {/* Learning Tab (Phase 2) */}
          <TabsContent value="learning" className="mt-6 space-y-6">
            {!skill?.isInstalled ? (
              <Card className="p-8 text-center">
                <Lightbulb className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Install skill to see learning progress
                </h3>
                <p className="text-gray-600 mb-4">
                  The skill must be installed to your twin to track learning and improvement.
                </p>
                <Button onClick={() => window.location.href = `/personal/skills/${skillId}`}>
                  Install Skill
                </Button>
              </Card>
            ) : skillMemory ? (
              <>
                {/* Learned Rules */}
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-5 h-5 text-yellow-600" />
                    <h3 className="text-lg font-semibold">Learned Behaviors</h3>
                    <Badge variant="secondary">{skillMemory.learnedRules?.length || 0} rules</Badge>
                  </div>
                  
                  {skillMemory.learnedRules && skillMemory.learnedRules.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 mb-4">
                        Based on your feedback, this skill has learned these behavioral rules:
                      </p>
                      {skillMemory.learnedRules.map((rule, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <CheckCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-900">{rule}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No learned rules yet</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Provide feedback on outputs to help the skill learn your preferences
                      </p>
                    </div>
                  )}
                </Card>

                {/* Feedback Patterns */}
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold">Feedback Patterns</h3>
                  </div>
                  
                  {skillMemory.feedbackPatterns && Object.keys(skillMemory.feedbackPatterns).some(key => skillMemory.feedbackPatterns[key] > 0) ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(skillMemory.feedbackPatterns).map(([pattern, count]) => (
                        count > 0 && (
                          <div key={pattern} className="p-4 bg-gray-50 rounded-lg border">
                            <div className="text-2xl font-bold text-gray-900">{count}</div>
                            <div className="text-sm text-gray-600 capitalize">{pattern.replace('_', ' ')}</div>
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No feedback recorded yet</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Use the feedback buttons (👍 👎) in conversations to record preferences
                      </p>
                    </div>
                  )}
                </Card>

                {/* Approved Examples */}
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ThumbsUp className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold">Successful Examples</h3>
                    <Badge variant="secondary">{skillMemory.approvedExamples?.length || 0} examples</Badge>
                  </div>
                  
                  {skillMemory.approvedExamples && skillMemory.approvedExamples.length > 0 ? (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 mb-4">
                        These are outputs you marked as good. The skill references these as examples of quality work.
                      </p>
                      {skillMemory.approvedExamples.slice(0, 3).map((example, index) => (
                        <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-xs text-gray-500 mb-2">
                            Input: {example.inputSummary}
                          </div>
                          <div className="text-sm text-gray-900">
                            {example.output.substring(0, 200)}
                            {example.output.length > 200 && '...'}
                          </div>
                        </div>
                      ))}
                      {skillMemory.approvedExamples.length > 3 && (
                        <p className="text-sm text-gray-500 text-center">
                          + {skillMemory.approvedExamples.length - 3} more examples
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600">No approved examples yet</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Click "👍 Good" on assistant responses to save them as examples
                      </p>
                    </div>
                  )}
                </Card>

                {/* Learning Progress Info */}
                <Card className="p-6 bg-blue-50 border-blue-200">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">How Learning Works</h4>
                      <ul className="space-y-2 text-sm text-blue-800">
                        <li>• <strong>Feedback buttons:</strong> Use 👍 👎 buttons to rate responses</li>
                        <li>• <strong>Pattern recognition:</strong> After 3+ similar feedback items, a rule is learned</li>
                        <li>• <strong>Approved examples:</strong> Good outputs become reference examples</li>
                        <li>• <strong>Continuous improvement:</strong> The skill adapts to your preferences over time</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              </>
            ) : (
              <Card className="p-8 text-center">
                <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No learning data yet
                </h3>
                <p className="text-gray-600">
                  Use this skill in conversations and provide feedback to start building its memory.
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-6 space-y-6">
            <div>
              <h3 className="font-semibold text-lg mb-4">{t('skills.manageSkill')}</h3>
              
              <div className="space-y-4">
                {/* Edit Skill (if custom) */}
                {skill.isCustom && (
                  <Card className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{t('skills.editSkill')}</h4>
                        <p className="text-sm text-gray-600 mt-1">{t('skills.editSkillDesc')}</p>
                      </div>
                      <Button onClick={() => navigate(`/personal/skills/edit/${skillId}`)}>
                        <Edit className="w-4 h-4 mr-2" />
                        {t('common.edit')}
                      </Button>
                    </div>
                  </Card>
                )}

                {/* Uninstall */}
                <Card className="p-4 border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-red-900">{t('skills.uninstallSkill')}</h4>
                      <p className="text-sm text-red-600 mt-1">{t('skills.uninstallSkillDesc')}</p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={handleUninstall}
                      disabled={processingAction}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('skills.uninstall')}
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
